import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  UserGroupIcon,
  EnvelopeIcon,
  CheckBadgeIcon,
  ListBulletIcon,
  Squares2X2Icon,
  MagnifyingGlassIcon,
  EyeIcon,
  CommandLineIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import i18n from '../i18n';


const AccountDetailModal = ({ account, onClose }) => {
  if (!account) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-fade-in">
      <div className="bg-white max-w-2xl w-full p-6 sm:p-8 md:p-10 border border-[#adc6ce]/40 shadow-2xl rounded-2xl animate-scale-in max-h-[90vh] overflow-y-auto relative">

        <div className="flex justify-between items-start mb-6 pb-4 border-b border-slate-100">
          <div className="flex gap-4 items-center">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-50 rounded-xl flex items-center justify-center text-[#155e75] flex-shrink-0">
              <UserGroupIcon className="w-8 h-8 sm:w-10 sm:h-10" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-slate-900 uppercase tracking-tight break-all">{account.account_name}</h2>
              <p className="text-[#155e75] font-bold text-xs sm:text-sm tracking-tight">{account.account_type}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg bg-slate-50"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-4 border-y border-slate-100">
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t('vat_number')}</p>
            <p className="text-slate-800 font-mono font-bold text-sm">{account.vat_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t('legal_rep')}</p>
            <p className="text-slate-800 font-bold text-sm">{account.legal_representative || 'N/A'}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t('registry_id')}</p>
            <p className="text-slate-800 font-mono font-bold text-sm break-all">{account.account_number}</p>
          </div>
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">{i18n.t('reg_country')}</p>
            <p className="text-slate-800 font-bold text-sm">{account.country_code}</p>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-8 py-3 rounded-xl bg-slate-100 text-slate-600 font-bold hover:bg-slate-200 transition-all text-xs uppercase tracking-wider"
          >
            {i18n.t('close_entry')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'grid'
  const [search, setSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get('/api/v1/accounts');
        setAccounts(response.data);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, []);

  const handleApprove = async (id) => {
    if (!window.confirm(i18n.t('confirm_approve'))) return;
    try {
      await axios.put(`/api/v1/accounts/${id}/approve`);
      setAccounts(accounts.map(acc => acc.id === id ? { ...acc, status: 'ACTIVE' } : acc));
    } catch (error) {
      console.error('Approval failed', error);
      alert(i18n.t('approve_failed'));
    }
  };

  const filteredAccounts = accounts.filter(acc =>
    acc.account_name.toLowerCase().includes(search.toLowerCase()) ||
    acc.account_number.toLowerCase().includes(search.toLowerCase()) ||
    acc.vat_number?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center py-40">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#155e75]" />
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in px-2 sm:px-4 md:px-0">
      {selectedAccount && <AccountDetailModal account={selectedAccount} onClose={() => setSelectedAccount(null)} />}

      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div className="text-center xl:text-left">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{i18n.t('registry_participants')}</h1>
          <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">{i18n.t('registry_participants_desc')}</p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">

          <div className="relative w-full sm:w-64">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder={i18n.t('search_participants')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-white border border-slate-200 rounded-xl shadow-sm py-2.5 pl-11 pr-4 text-slate-700 text-xs outline-none focus:border-[#155e75] transition-all w-full"
            />
          </div>

          <div className="flex p-1 bg-slate-100 rounded-xl border border-slate-200 self-center">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white text-[#155e75] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Squares2X2Icon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white text-[#155e75] shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <ListBulletIcon className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={() => window.location.href = '/register'}
            className="bg-[#155e75] hover:bg-[#0e4e61] text-white text-xs font-bold uppercase tracking-wider px-6 py-3.5 rounded-xl shadow-lg shadow-[#155e75]/10 transition-all text-center"
          >
            {i18n.t('new_entity')}
          </button>
        </div>
      </div>

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAccounts.map((account) => (
            <div key={account.id} className="bg-white p-6 rounded-2xl border border-[#adc6ce]/30 hover:border-[#155e75]/40 shadow-sm hover:shadow-md transition-all duration-300 group flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-blue-50 p-3 rounded-xl text-[#155e75] transition-transform group-hover:scale-105">
                    <UserGroupIcon className="w-7 h-7" />
                  </div>
                  <div className="flex flex-col items-end">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-bold ${account.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                      {account.status}
                    </span>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-1.5">{account.account_type}</p>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-slate-900 mb-1 tracking-tight break-all">{account.account_name}</h3>
                <p className="text-[10px] font-bold text-[#6a99a7] uppercase tracking-widest mb-6">ID: {account.account_number}</p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <EnvelopeIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-xs font-semibold text-slate-600 truncate">{account.email}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-1.5 opacity-55">
                  <CheckBadgeIcon className={`w-4 h-4 ${account.status === 'ACTIVE' ? 'text-emerald-600' : 'text-slate-400'}`} />
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">EECS</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedAccount(account)}
                    className="p-2 rounded-xl bg-slate-50 text-slate-400 hover:text-[#155e75] hover:bg-blue-50 transition-all border border-slate-200"
                  >
                    <EyeIcon className="w-4.5 h-4.5" />
                  </button>
                  {account.status === 'PENDING' && (
                    <button
                      onClick={() => handleApprove(account.id)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider px-4 py-2 rounded-xl transition-all"
                    >
                      {i18n.t('activate')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (

        <div className="bg-white border border-[#adc6ce]/30 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="ent-table w-full text-left text-xs">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100">
                  <th className="py-4 px-5">{i18n.t('member_id')}</th>
                  <th className="py-4">{i18n.t('legal_name')}</th>
                  <th className="py-4">{i18n.t('classification')}</th>
                  <th className="py-4">{i18n.t('vat_id')}</th>
                  <th className="text-center py-4">{i18n.t('status_col')}</th>
                  <th className="text-right py-4 px-5">{i18n.t('operation_col')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAccounts.map((account) => (
                  <tr key={account.id} className="hover:bg-slate-50/20 transition-colors">
                    <td className="py-4 px-5 font-mono text-[11px] text-slate-500">{account.account_number}</td>
                    <td className="py-4">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-800">{account.account_name}</span>
                        <span className="text-[10px] text-slate-400 font-semibold">{account.email}</span>
                      </div>
                    </td>
                    <td className="py-4">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        {account.account_type}
                      </span>
                    </td>
                    <td className="py-4 text-xs text-slate-500 font-mono font-bold">
                      {account.vat_number || 'N/A'}
                    </td>
                    <td className="text-center py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-bold ${account.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                        {account.status}
                      </span>
                    </td>
                    <td className="text-right py-4 px-5">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => setSelectedAccount(account)}
                          className="p-1.5 bg-slate-50 text-slate-400 hover:text-[#155e75] rounded-lg border border-slate-200"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {account.status === 'PENDING' && (
                          <button
                            onClick={() => handleApprove(account.id)}
                            className="bg-emerald-600 text-white p-1.5 rounded-lg hover:bg-emerald-700 transition-colors"
                            title={i18n.t('activate')}
                          >
                            <CheckBadgeIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredAccounts.length === 0 && (
        <div className="p-16 sm:p-24 border border-dashed border-[#adc6ce]/60 rounded-2xl text-center flex flex-col items-center bg-white shadow-sm">
          <CommandLineIcon className="w-12 h-12 text-slate-200 mb-4" />
          <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2">{i18n.t('no_participants')}</h3>
          <p className="text-slate-400 text-xs font-semibold leading-relaxed max-w-xs">{i18n.t('no_participants_desc')}</p>
        </div>
      )}
    </div>
  );
}